import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  closePanicIncident,
  fetchPanicIncidents,
  fetchPanicSnapshotBlob,
} from '../api/panic';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import HikvisionLiveVideo from '../components/dashboard/HikvisionLiveVideo';
import { StatusDot } from '../components/dashboard/schoolEquipmentChrome';
import { surface } from '../components/ui/surface';
import { usePanicResponse } from '../context/PanicResponseContext';

const HISTORY_PAGE_SIZE = 20;

const statusTone = {
  open: 'critical',
  accepted: 'warning',
  closed: 'muted',
};

const statusBadge = (status, t) => (
  <StatusDot tone={statusTone[status] || 'muted'} label={t(`panic.status.${status}`)} />
);

const PanicIncidents = ({ setIsAuthenticated, userEmail, userRole, canRespondToIncidents, onLogout }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isSidebarVisible, setSidebarVisible] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 1024 : true
  );
  const [active, setActive] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [snapshotUrl, setSnapshotUrl] = useState(null);
  const [resolution, setResolution] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const { attend, busy } = usePanicResponse();
  const toggleSidebar = () => setSidebarVisible((prev) => !prev);
  const canMutate = Boolean(canRespondToIncidents);

  const loadActive = useCallback(async () => {
    try {
      const { data } = await fetchPanicIncidents({ status: 'active', limit: 100 });
      setActive(Array.isArray(data) ? data : []);
    } catch {
      setActive([]);
    }
  }, []);

  // История уезжает на сервер целиком: инцидентов за год больше, чем имеет смысл
  // держать в браузере, а листают её страницами.
  const loadHistory = useCallback(async (page) => {
    try {
      const { data, pagination } = await fetchPanicIncidents({
        status: 'closed',
        limit: HISTORY_PAGE_SIZE,
        page,
      });
      setHistory(Array.isArray(data) ? data : []);
      setHistoryTotal(pagination?.total ?? 0);
    } catch {
      setHistory([]);
      setHistoryTotal(0);
    }
  }, []);

  const reload = useCallback(async (page) => {
    setLoading(true);
    try {
      await Promise.all([loadActive(), loadHistory(page)]);
    } finally {
      setLoading(false);
    }
  }, [loadActive, loadHistory]);

  useEffect(() => {
    reload(historyPage);
    const interval = setInterval(() => reload(historyPage), 30000);
    return () => clearInterval(interval);
  }, [reload, historyPage]);

  // Инцидент мог сменить руки, пока журнал открыт: пусть список узнаёт об этом
  // сразу, а не на следующем опросе.
  useEffect(() => {
    const onUpdate = () => reload(historyPage);
    window.addEventListener('panicIncidentUpdated', onUpdate);
    return () => window.removeEventListener('panicIncidentUpdated', onUpdate);
  }, [reload, historyPage]);

  useEffect(() => {
    if (!selectedId) {
      setSnapshotUrl(null);
      return undefined;
    }
    let url = null;
    fetchPanicSnapshotBlob(selectedId)
      .then((blob) => {
        url = blob;
        setSnapshotUrl(blob);
      })
      .catch(() => setSnapshotUrl(null));
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [selectedId]);

  const selected = [...active, ...history].find((i) => i.id === selectedId) || null;
  const historyPages = Math.max(Math.ceil(historyTotal / HISTORY_PAGE_SIZE), 1);

  // Вернуться в тревогу можно к своей: чужой Разговор перехвату не подлежит.
  const isAttendable = (incident) =>
    incident?.status === 'open'
    || (incident?.status === 'accepted' && incident.accepted_by_email === userEmail);

  // Разбор идёт в модальном окне тревоги, а не в журнале: журнал только приводит
  // к нему Оператора.
  const goToSchool = (incident) => {
    const schoolIp = incident?.school_ip;
    if (!schoolIp) return;
    navigate(`/schools/${encodeURIComponent(schoolIp)}`);
  };

  const handleAttend = async (incident) => {
    await attend(incident);
    await reload(historyPage);
  };

  const handleClose = async () => {
    if (!selectedId || !resolution.trim()) return;
    setActionLoading(true);
    try {
      await closePanicIncident(selectedId, resolution.trim());
      setResolution('');
      setSelectedId(null);
      await reload(historyPage);
    } finally {
      setActionLoading(false);
    }
  };

  const renderRow = (inc) => (
    <button
      key={inc.id}
      type="button"
      onClick={() => setSelectedId(inc.id)}
      className={`flex w-full items-center gap-4 rounded-2xl px-4 py-3 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${
        selectedId === inc.id
          ? 'bg-white/[0.1]'
          : `${surface.card} hover:bg-white/[0.06]`
      }`}
    >
      <div className="flex-1 min-w-0">
        <p className="font-medium text-white truncate">{inc.school}</p>
        <p className="text-xs text-muted-400">{inc.station_name} · {new Date(inc.last_trigger_at).toLocaleString()}</p>
      </div>
      {statusBadge(inc.status, t)}
      <span className="text-xs text-muted-400">{inc.trigger_count}×</span>
    </button>
  );

  return (
    <div className="relative flex min-h-screen flex-col overflow-clip bg-surface-base text-muted-100">
      <div className="pointer-events-none absolute inset-0 -z-10 bg-brand-radial opacity-50" />
      <Header isSidebarVisible={isSidebarVisible} toggleSidebar={toggleSidebar} userEmail={userEmail} userRole={userRole} />
      <div className="flex flex-1">
        <Sidebar onLogout={onLogout} userRole={userRole} isSidebarVisible={isSidebarVisible} onClose={() => setSidebarVisible(false)} />
        <main className="flex min-w-0 flex-1 flex-col gap-6 bg-surface-base/60 px-3 py-4 sm:px-4 sm:py-6 lg:px-10">
          <div className="space-y-6">
            <div>
              <h1 className="font-display text-xl text-white md:text-2xl">{t('panic.title')}</h1>
              <p className="mt-1 text-sm text-muted-400">{t('panic.subtitle')}</p>
            </div>

            {loading && <p className="text-sm text-muted-400">{t('common.loading')}</p>}

            {/* Снимок и live — сразу под активными: история длинная, и искать
                разбор под ней приходилось листать. */}
            <section className="space-y-3">
              <h2 className="font-display text-lg text-white">{t('panic.active')}</h2>
              {active.length === 0 ? (
                <p className="text-sm text-muted-400">{t('panic.noActive')}</p>
              ) : (
                <div className="grid gap-3 lg:grid-cols-2">{active.map(renderRow)}</div>
              )}
            </section>

            {selected && (
              <div className={`space-y-4 rounded-3xl p-6 ${surface.section}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="font-display text-lg text-white">#{selected.id} — {selected.school}</h3>
                  {statusBadge(selected.status, t)}
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  {snapshotUrl && (
                    <img src={snapshotUrl} alt="" className="w-full rounded-2xl" />
                  )}
                  {selected.device_id && selected.video_enabled !== false && (
                    <HikvisionLiveVideo deviceId={selected.device_id} videoEnabled defaultChannel="101" />
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={!selected.school_ip}
                    onClick={() => goToSchool(selected)}
                    className="inline-flex items-center gap-2 rounded-xl bg-white/[0.06] px-4 py-2.5 text-sm font-medium text-muted-100 transition hover:bg-white/[0.1] hover:text-white disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                  >
                    <span className="material-icons text-base">school</span>
                    {t('panic.goToSchool')}
                  </button>
                {canMutate && isAttendable(selected) && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => handleAttend(selected)}
                    className="inline-flex items-center rounded-xl bg-critical/15 px-4 py-2.5 text-sm font-medium text-critical transition hover:bg-critical/25 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                  >
                    {selected.status === 'open' ? t('panic.accept') : t('panic.backToConversation')}
                  </button>
                )}
                </div>

                {selected.status === 'accepted' && selected.accepted_by_email && !isAttendable(selected) && (
                  <p className="text-sm text-muted-300">
                    {t('panic.takenBy', { who: selected.accepted_by_email })}
                  </p>
                )}

                {canMutate && selected.status === 'accepted' && (
                  <div className="space-y-2">
                    <textarea
                      value={resolution}
                      onChange={(e) => setResolution(e.target.value)}
                      placeholder={t('panic.resolutionPlaceholder')}
                      rows={3}
                      className="w-full rounded-xl bg-white/[0.04] p-3 text-sm text-white placeholder:text-muted-500 outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                    />
                    <button
                      type="button"
                      disabled={actionLoading || !resolution.trim()}
                      onClick={handleClose}
                      className="inline-flex items-center rounded-xl bg-white/[0.06] px-4 py-2.5 text-sm font-medium text-muted-100 transition hover:bg-white/[0.1] hover:text-white disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                    >
                      {t('panic.close')}
                    </button>
                  </div>
                )}

                {selected.resolution && (
                  <p className="text-sm text-muted-300"><strong>{t('panic.resolution')}:</strong> {selected.resolution}</p>
                )}
              </div>
            )}

            <section className="space-y-3">
              <h2 className="font-display text-lg text-white">{t('panic.history')}</h2>
              {history.length === 0 ? (
                <p className="text-sm text-muted-400">{t('panic.noHistory')}</p>
              ) : (
                <>
                  <div className="grid gap-3 lg:grid-cols-2">{history.map(renderRow)}</div>
                  <div className="flex items-center justify-between gap-3 pt-1">
                    <button
                      type="button"
                      disabled={historyPage <= 1}
                      onClick={() => setHistoryPage((page) => Math.max(page - 1, 1))}
                      className="rounded-xl bg-white/[0.04] px-3 py-1.5 text-xs text-muted-200 transition hover:bg-white/[0.08] hover:text-white disabled:opacity-40"
                    >
                      {t('common.previous')}
                    </button>
                    <span className="text-xs text-muted-400">
                      {t('panic.historyPage', { page: historyPage, pages: historyPages, total: historyTotal })}
                    </span>
                    <button
                      type="button"
                      disabled={historyPage >= historyPages}
                      onClick={() => setHistoryPage((page) => Math.min(page + 1, historyPages))}
                      className="rounded-xl bg-white/[0.04] px-3 py-1.5 text-xs text-muted-200 transition hover:bg-white/[0.08] hover:text-white disabled:opacity-40"
                    >
                      {t('common.next')}
                    </button>
                  </div>
                </>
              )}
            </section>
          </div>
        </main>
      </div>
    </div>
  );
};

export default PanicIncidents;
